import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Slider } from '@monksflow/monks-ui';
import {
  useAnnotationFullscreen,
  useAnnotationSolo,
  useMediaPlayer,
  useReplayAnnotation,
} from '@/features/comment-on-file';
import { throttle } from '@/shared/utils/throttle';
import { FullscreenToggle } from '../Fullscreen';
import { ControlGroup, ControlGroupSeparator, HoverControlsPill } from '../HoverControlsPill';
import { useMarkers } from './useMarkers';
import { useMediaElementControls } from './useMediaElementControls';
import { VideoPlayPauseControls } from './VideoPlayPauseControls';
import { useTranslation } from 'react-i18next';

const SCRUB_CATCHUP_TOLERANCE_PERCENT = 0.5;
const SOLO_CLEAR_THRESHOLD_SECONDS = 0.25;

interface Props {
  annotationsEnabled?: boolean;
  showFullscreen?: boolean;
}

export function VideoControls({ annotationsEnabled = true, showFullscreen = true }: Props = {}) {
  const { t } = useTranslation('projects');

  const { mediaElement } = useMediaPlayer();
  const { state, setProgressPercent } = useMediaElementControls(mediaElement);
  const markers = useMarkers();
  const replay = useReplayAnnotation();
  const { soloCommentId, clearSolo } = useAnnotationSolo();
  const { isFullscreen } = useAnnotationFullscreen();

  const progressPercent =
    state.duration > 0 ? Math.min(100, (state.currentTime / state.duration) * 100) : 0;

  const [scrubValue, setScrubValue] = useState<number | null>(null);
  const [rafPercent, setRafPercent] = useState<number | null>(null);

  useEffect(() => {
    if (!mediaElement || !state.isPlaying) {
      setRafPercent(null);
      return;
    }
    let raf: number;
    const update = () => {
      if (mediaElement.duration > 0) {
        setRafPercent(Math.min(100, (mediaElement.currentTime / mediaElement.duration) * 100));
      }
      raf = requestAnimationFrame(update);
    };
    raf = requestAnimationFrame(update);
    return () => cancelAnimationFrame(raf);
  }, [mediaElement, state.isPlaying]);

  const displayPercent = scrubValue ?? rafPercent ?? progressPercent;

  const [controlsVisible, setControlsVisible] = useState(true);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPlayingRef = useRef(state.isPlaying);
  isPlayingRef.current = state.isPlaying;

  const scheduleHide = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), 1500);
  }, []);

  useEffect(() => {
    if (!state.isPlaying) {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      setControlsVisible(true);
      return;
    }
    setControlsVisible(true);
    scheduleHide();
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [state.isPlaying, scheduleHide]);

  useEffect(() => {
    const container = mediaElement?.parentElement;
    if (!container) return;
    const onMouseMove = () => {
      if (!isPlayingRef.current) return;
      setControlsVisible(true);
      scheduleHide();
    };
    container.addEventListener('mousemove', onMouseMove);
    return () => container.removeEventListener('mousemove', onMouseMove);
  }, [mediaElement, scheduleHide]);

  useEffect(() => {
    if (scrubValue === null) return;
    if (Math.abs(scrubValue - progressPercent) < SCRUB_CATCHUP_TOLERANCE_PERCENT) {
      setScrubValue(null);
    }
  }, [scrubValue, progressPercent]);

  useEffect(() => {
    if (!soloCommentId || !annotationsEnabled || !mediaElement) return;
    const soloMarker = markers.find((m) => m.commentId === soloCommentId);
    if (!soloMarker) return;
    const check = throttle(() => {
      if (Math.abs(mediaElement.currentTime - soloMarker.timecode) > SOLO_CLEAR_THRESHOLD_SECONDS)
        clearSolo();
    }, 10);
    let raf: number;
    const loop = () => {
      check();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [soloCommentId, annotationsEnabled, mediaElement, markers, clearSolo]);

  const sliderValue = useMemo(() => [displayPercent], [displayPercent]);
  const onSliderChange = useCallback(
    ([v]: number[]) => {
      setScrubValue(v);
      setProgressPercent(v);
    },
    [setProgressPercent],
  );

  void replay;

  return (
    <>
      {!isFullscreen && (
        <HoverControlsPill visible={controlsVisible} bottomClassName="bottom-4">
          <ControlGroup>
            <VideoPlayPauseControls />
            {showFullscreen && (
              <>
                <ControlGroupSeparator />
                <FullscreenToggle />
              </>
            )}
          </ControlGroup>
        </HoverControlsPill>
      )}

      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 pt-6">
        <div className="pointer-events-auto relative flex items-center">
          <Slider
            className="cursor-pointer **:data-[slot='slider-thumb']:hidden **:data-[slot='slider-track']:rounded-none **:data-[slot='slider-track']:shadow-none **:data-[slot='slider-track']:bg-surface-emphasis **:data-[slot='slider-range']:rounded-none"
            value={sliderValue}
            min={0}
            max={100}
            step={0.1}
            onValueChange={onSliderChange}
            aria-label={t('projectDetail.videoControls.seek')}
          />
        </div>
      </div>
    </>
  );
}
